import React, { useEffect, useRef, useState } from 'react';
import { X, ChevronDown, Loader2, SlidersHorizontal } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { getLagerClassificationOptions } from 'core/api/apiService';

/**
 * MerkmaleFilter — Dynamischer Klassifizierungs-Merkmals-Filterbereich (Phase 3c).
 *
 * Props:
 *   user            Object   — Auth-User (fuer API-Call)
 *   werk            string   — Ausgewaehltes Werk
 *   selectedWg      string[] — Aktuell gewaehlte Warengruppen (CSV fuer Query)
 *   merkmale        Array<{name, values?:[], min?, max?}>
 *   onMerkmaleChange (merkmale: Array) => void
 */
const MerkmaleFilter = ({ user, werk, selectedWg, merkmale, onMerkmaleChange }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Welches Merkmal-Dropdown aktuell offen ist (by merkmal_name)
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = useRef({});

  const hasWg = Array.isArray(selectedWg) && selectedWg.length > 0;

  // Optionen laden wenn Werk oder Warengruppen sich aendern
  useEffect(() => {
    if (!werk || !hasWg) {
      setOptions([]);
      setError(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const wgCsv = selectedWg.join(',');
        const data = await getLagerClassificationOptions(user, werk, wgCsv);
        if (cancelled) return;
        setOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || t('lm.merkmaleLoadError'));
          setOptions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [werk, selectedWg.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Beim Wechsel der Warengruppen: nicht mehr passende Merkmale verwerfen
  useEffect(() => {
    if (!hasWg) {
      if (Array.isArray(merkmale) && merkmale.length > 0) {
        onMerkmaleChange([]);
      }
      return;
    }
    // Nach dem Laden: nicht mehr vorhandene Merkmale entfernen
    if (options.length > 0 && Array.isArray(merkmale) && merkmale.length > 0) {
      const validNames = new Set(options.map((o) => o.merkmal_name));
      const filtered = merkmale.filter((m) => validNames.has(m.name));
      if (filtered.length !== merkmale.length) {
        onMerkmaleChange(filtered);
      }
    }
  }, [options]); // eslint-disable-line react-hooks/exhaustive-deps

  // Click-Outside: offenes Dropdown schliessen
  useEffect(() => {
    if (!openDropdown) return;
    const handle = (e) => {
      const ref = dropdownRefs.current[openDropdown];
      if (ref && !ref.contains(e.target)) setOpenDropdown(null);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [openDropdown]);

  // ---- Hilfsfunktionen ----

  const getMerkmal = (name) => (Array.isArray(merkmale) ? merkmale.find((m) => m.name === name) : undefined);

  const setTextMerkmal = (name, values) => {
    const rest = Array.isArray(merkmale) ? merkmale.filter((m) => m.name !== name) : [];
    if (values.length === 0) {
      onMerkmaleChange(rest);
    } else {
      onMerkmaleChange([...rest, { name, values }]);
    }
  };

  const setNumericMerkmal = (name, field, value) => {
    const existing = getMerkmal(name) || { name };
    const updated = { ...existing, [field]: value === '' ? undefined : Number(value) };
    // Wenn beide leer, Merkmal entfernen
    const hasMin = updated.min != null;
    const hasMax = updated.max != null;
    const rest = Array.isArray(merkmale) ? merkmale.filter((m) => m.name !== name) : [];
    if (!hasMin && !hasMax) {
      onMerkmaleChange(rest);
    } else {
      onMerkmaleChange([...rest, updated]);
    }
  };

  const removeMerkmal = (name) => {
    onMerkmaleChange((Array.isArray(merkmale) ? merkmale : []).filter((m) => m.name !== name));
  };

  // ---- Style-Variablen ----
  const chipBase = 'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 cursor-pointer select-none';
  const chipActive = isDarkMode ? 'bg-brand/25 border-brand/50 text-blue-200' : 'bg-brand/10 border-brand/40 text-brand';
  const chipInactive = isDarkMode
    ? 'bg-white/8 border-white/15 text-white/60 hover:bg-white/12'
    : 'bg-white border-slate-200 text-slate-600 hover:border-brand/30 hover:text-brand';

  const inputCls = `text-xs rounded-lg border outline-none px-2 py-1.5 w-24 ${
    isDarkMode
      ? 'bg-white/8 border-white/15 text-white placeholder-white/30 focus:border-brand/50'
      : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400 focus:border-brand/50'
  }`;

  const dropdownCls = `absolute z-40 mt-1 w-56 rounded-xl shadow-lg border overflow-hidden ${
    isDarkMode
      ? 'bg-gray-900 border-white/15 shadow-[0_8px_32px_rgba(0,0,0,0.5)]'
      : 'bg-white border-slate-200 shadow-lg'
  }`;

  const labelCls = `text-xs font-medium ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`;

  // ---- Kein Werk oder keine Warengruppe ----
  if (!werk || !hasWg) {
    return (
      <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
        <div className={`flex items-center gap-2 text-xs ${isDarkMode ? 'text-white/35' : 'text-slate-400'}`}>
          <SlidersHorizontal className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{t('lm.merkmaleSelectWg')}</span>
        </div>
      </div>
    );
  }

  // ---- Loading ----
  if (loading) {
    return (
      <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
        <div className={`flex items-center gap-2 text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          <span>{t('lm.merkmaleLoading')}</span>
        </div>
      </div>
    );
  }

  // ---- Error ----
  if (error) {
    return (
      <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
        <p className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}>{t('lm.merkmaleError')}</p>
      </div>
    );
  }

  // ---- Leer (keine Merkmale im Werk/Warengruppe) ----
  if (options.length === 0) {
    return (
      <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
        <div className={`flex items-center gap-2 text-xs ${isDarkMode ? 'text-white/35' : 'text-slate-400'}`}>
          <SlidersHorizontal className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{t('lm.merkmaleEmpty')}</span>
        </div>
      </div>
    );
  }

  // ---- Aktive Merkmals-Chips ----
  const activeChips = (Array.isArray(merkmale) ? merkmale : []).map((m) => {
    const opt = options.find((o) => o.merkmal_name === m.name);
    let label = m.name;
    if (opt && !opt.is_numeric && Array.isArray(m.values) && m.values.length > 0) {
      label = `${m.name}: ${m.values.join(', ')}`;
    } else if (opt && opt.is_numeric) {
      const parts = [];
      if (m.min != null) parts.push(`${t('lm.merkmaleVon')} ${m.min}`);
      if (m.max != null) parts.push(`${t('lm.merkmaleBis')} ${m.max}`);
      if (parts.length > 0) label = `${m.name}: ${parts.join(' ')}`;
      if (opt.unit) label += ` ${opt.unit}`;
    }
    return (
      <span key={`mk-${m.name}`} className={`${chipBase} ${chipActive}`}>
        <span className="max-w-[180px] truncate">{label}</span>
        <button
          type="button"
          onClick={() => removeMerkmal(m.name)}
          className="ml-0.5 hover:text-red-400 transition-colors flex-shrink-0"
          aria-label={t('common.remove')}
        >
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  });

  return (
    <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
      {/* Abschnitts-Label */}
      <p className={`text-[11px] font-semibold uppercase tracking-wider mb-2 ${isDarkMode ? 'text-white/35' : 'text-slate-400'}`}>
        {t('lm.merkmaleTitle')}
      </p>

      {/* Merkmal-Controls */}
      <div className="flex flex-wrap items-start gap-2">
        {options.map((opt) => {
          const currentMerkmal = getMerkmal(opt.merkmal_name);
          const isActive = !!currentMerkmal;

          if (!opt.is_numeric) {
            // Mehrfachauswahl-Dropdown
            const safeValues = Array.isArray(opt.values) ? opt.values : [];
            const selectedValues = Array.isArray(currentMerkmal?.values) ? currentMerkmal.values : [];
            const isOpen = openDropdown === opt.merkmal_name;

            return (
              <div
                key={opt.merkmal_name}
                className="relative"
                ref={(el) => { dropdownRefs.current[opt.merkmal_name] = el; }}
              >
                <button
                  type="button"
                  onClick={() => setOpenDropdown(isOpen ? null : opt.merkmal_name)}
                  className={`${chipBase} ${isActive ? chipActive : chipInactive} gap-1.5`}
                >
                  <span className="max-w-[120px] truncate">{opt.merkmal_name}</span>
                  {selectedValues.length > 0 && (
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                      isDarkMode ? 'bg-brand/40 text-blue-100' : 'bg-brand text-white'
                    }`}>
                      {selectedValues.length}
                    </span>
                  )}
                  <ChevronDown className={`w-3 h-3 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className={dropdownCls}>
                    <div className="max-h-48 overflow-y-auto py-1">
                      {safeValues.length === 0 ? (
                        <p className={`px-3 py-2 text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
                          {t('lm.empty')}
                        </p>
                      ) : (
                        safeValues.map((val) => {
                          const checked = selectedValues.includes(val);
                          const newValues = checked
                            ? selectedValues.filter((v) => v !== val)
                            : [...selectedValues, val];
                          return (
                            <button
                              key={val}
                              type="button"
                              onClick={() => setTextMerkmal(opt.merkmal_name, newValues)}
                              className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors ${
                                isDarkMode ? 'hover:bg-white/8 text-white/80' : 'hover:bg-slate-50 text-slate-700'
                              } ${checked ? (isDarkMode ? 'bg-brand/15' : 'bg-brand/5') : ''}`}
                            >
                              <span className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center ${
                                checked ? 'bg-brand border-brand' : isDarkMode ? 'border-white/30' : 'border-slate-300'
                              }`}>
                                {checked && <X className="w-2.5 h-2.5 text-white" />}
                              </span>
                              <span className="truncate">{val}</span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          }

          // Numerischer Bereich (von/bis)
          const minVal = currentMerkmal?.min ?? '';
          const maxVal = currentMerkmal?.max ?? '';
          const unitLabel = opt.unit ? ` (${opt.unit})` : '';

          return (
            <div key={opt.merkmal_name} className="flex items-center gap-1.5 flex-wrap">
              <span className={labelCls}>{opt.merkmal_name}{unitLabel}:</span>
              <span className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('lm.merkmaleVon')}</span>
              <input
                type="number"
                value={minVal}
                onChange={(e) => setNumericMerkmal(opt.merkmal_name, 'min', e.target.value)}
                placeholder={opt.num_min != null ? String(opt.num_min) : '–'}
                className={inputCls}
                aria-label={`${opt.merkmal_name} ${t('lm.merkmaleVon')}`}
              />
              <span className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('lm.merkmaleBis')}</span>
              <input
                type="number"
                value={maxVal}
                onChange={(e) => setNumericMerkmal(opt.merkmal_name, 'max', e.target.value)}
                placeholder={opt.num_max != null ? String(opt.num_max) : '–'}
                className={inputCls}
                aria-label={`${opt.merkmal_name} ${t('lm.merkmaleBis')}`}
              />
            </div>
          );
        })}
      </div>

      {/* Aktive Merkmals-Chips */}
      {activeChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {activeChips}
        </div>
      )}
    </div>
  );
};

export default MerkmaleFilter;
